import type React from "react";

import type { AutocompleteProps } from "@mui/material/Autocomplete";
import Autocomplete, { autocompleteClasses, createFilterOptions } from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import { styled } from "@mui/material/styles";
import { Controller, useFormContext } from "react-hook-form";

import { Container, ErrorText, StyledFormControl } from "../inputs/MUIAutocomplete/MUIAutocomplete";

import { ListboxComponent } from "./ListboxComponent";

type Option = {
  label: string;
  value: string;
};

interface Props extends Omit<AutocompleteProps<Option, false, boolean, boolean>, "renderOption"> {
  error?: string;
  name: string;
  required?: boolean;
}

export const MUIAutocompleteVirtualized: React.FC<Props> = ({
  error,
  disabled,
  name,
  required,
  options,
  renderInput,
  ...rest
}) => {
  const { control, setValue } = useFormContext();

  return (
    <Container>
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        render={({ field }) => {
          return (
            <StyledFormControl variant="outlined" fullWidth disabled={disabled}>
              <Autocomplete
                {...field}
                disableListWrap
                disabled={disabled}
                options={options}
                filterOptions={createFilterOptions({
                  stringify: option => {
                    return `${option.value} ${option.label}`;
                  },
                })}
                getOptionLabel={option => {
                  if (option === null || option === undefined) {
                    return "";
                  }
                  if (typeof option === "string") {
                    return options.find(o => o.value === option)?.label ?? option;
                  }
                  return option.label || "";
                }}
                renderInput={renderInput}
                renderOption={(props, option, state) => [props, option, state.index] as React.ReactNode}
                onChange={(_event, option) => {
                  if (option === null) {
                    setValue(name, null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  } else if (typeof option === "string") {
                    setValue(name, option, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  } else {
                    setValue(name, option.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
                isOptionEqualToValue={(option, value) => {
                  return option.value === value.value;
                }}
                slots={{
                  popper: StyledPopper,
                }}
                slotProps={{
                  listbox: {
                    component: ListboxComponent,
                  },
                }}
                {...rest}
              />
            </StyledFormControl>
          );
        }}
      />
      {!!error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});
